// src/views.js
// All view renderers. Register themselves with the router.

import { state, store, importLegacyBackup } from './lib/store.js';
import {
  escapeHtml, fmtMoney, fmtDealMoney, fmtDate, relativeDate, initials,
  getCompany, getContact, getDeal, getTicket, getAttachment, contactName,
  dealStageName, dealStatus, ticketStageName, todayStr, toast, runAsync,
  showModal, closeModal, fmtFileSize,
  convertCurrency, convertDealValue, sumInCurrency, fxRatesAvailable, isAdmin
} from './lib/helpers.js';
import { registerView, navigate, render } from './lib/router.js';
import {
  editCompany, editContact, editDeal, editTicket, editActivity,
  renderQuickNote, setupQuickNote, renderTimeline, setupTimelineFilters,
  renderAttachmentsPanel,
  setDealStage, setDealStatus, setTicketStage, markDealWon, closeTicket
} from './lib/modals.js';

// -------------------------------------------------------------
// shared: kanban drag/drop
// -------------------------------------------------------------
function setupKanbanDragDrop(cardSel, datasetKey, onDrop) {
  let dragId = null;
  document.querySelectorAll(cardSel).forEach(card => {
    card.addEventListener('dragstart', (e) => {
      dragId = card.dataset[datasetKey];
      card.classList.add('dragging');
      e.dataTransfer.effectAllowed = 'move';
    });
    card.addEventListener('dragend', () => { card.classList.remove('dragging'); dragId = null; });
  });
  document.querySelectorAll('.stage-body').forEach(zone => {
    zone.addEventListener('dragover', (e) => { e.preventDefault(); zone.classList.add('drop-target'); e.dataTransfer.dropEffect = 'move'; });
    zone.addEventListener('dragleave', () => zone.classList.remove('drop-target'));
    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      zone.classList.remove('drop-target');
      if (dragId) onDrop(dragId, zone.dataset.stage);
    });
  });
}

// -------------------------------------------------------------
// shared activity row for lists/panels
// -------------------------------------------------------------
function renderActivityRowMini(a, showDate) {
  const c = getContact(a.contactId);
  const d = getDeal(a.dealId);
  const t = a.ticketId ? getTicket(a.ticketId) : null;
  const overdue = !a.done && a.date && a.date < todayStr();
  const whenLabel = showDate
    ? (overdue ? '<span style="color:var(--bad); font-weight:600;">' + relativeDate(a.date) + '</span>' : relativeDate(a.date))
    : new Date(a.createdAt).toLocaleDateString(undefined,{day:'2-digit',month:'short'});
  const linkCtx = [c?contactName(c):null, d?d.title:null, t?t.subject:null].filter(Boolean).join(' · ');
  return `<li style="padding: 11px 0; border-bottom: 1px dashed var(--border); display: grid; grid-template-columns: 90px 1fr auto; gap: 12px; align-items: start;">
    <div style="font-family:'JetBrains Mono',monospace; font-size:11px; color:var(--ink-muted);">${whenLabel}</div>
    <div style="font-size:13.5px; color:var(--ink);">
      <div>${escapeHtml(a.subject || '(no subject)')}</div>
      ${linkCtx ? '<div class="inline-note" style="margin-top:3px;">' + escapeHtml(linkCtx) + '</div>' : ''}
    </div>
    <div class="chip">${escapeHtml(a.type || 'note')}</div>
  </li>`;
}

// =============================================================
// DASHBOARD
// =============================================================
// Group an array of deals by their effective currency, summing values.
// Returns: { ZAR: 1200000, USD: 45000 } sorted by total descending.
function sumByCurrency(deals, valueFn) {
  const buckets = {};
  for (const d of deals) {
    const cur = d.currency || state.settings.currency;
    const v = valueFn ? valueFn(d) : (Number(d.value) || 0);
    buckets[cur] = (buckets[cur] || 0) + v;
  }
  return buckets;
}
function renderCurrencyTotals(buckets, opts) {
  const entries = Object.entries(buckets).filter(([_,v]) => v > 0).sort((a,b) => b[1] - a[1]);
  if (!entries.length) return opts && opts.zero ? opts.zero : '—';
  return entries.map(([cur, val]) => fmtMoney(val, cur)).join(' · ');
}
// Stacked variant: one line per currency, currency code dimmed and aligned left,
// value bold and aligned right. Used in the dashboard stat cards so multiple
// currencies don't get squashed onto one row.
function renderCurrencyStack(buckets, opts) {
  const entries = Object.entries(buckets).filter(([_,v]) => v > 0).sort((a,b) => b[1] - a[1]);
  if (!entries.length) return `<div style="font-size: 32px; color: var(--ink-muted);">—</div>`;
  return `<div style="display: flex; flex-direction: column; gap: 4px;">
    ${entries.map(([cur, val]) => `
      <div style="display: flex; justify-content: space-between; align-items: baseline; gap: 10px;">
        <span style="font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--ink-muted); letter-spacing: 0.1em;">${escapeHtml(cur)}</span>
        <span style="font-family: 'Outfit', sans-serif; font-size: 22px; font-weight: 600; color: var(--ink); font-variant-numeric: tabular-nums;">${escapeHtml(Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 }))}</span>
      </div>
    `).join('')}
  </div>`;
}

// Render a converted single-currency total. Used when the user selects a display
// currency in the dashboard toggle. Returns big-number HTML.
function renderConvertedTotal(deals, toCur, valueFn) {
  const { total, missing } = sumInCurrency(deals, toCur, valueFn);
  return `<div style="display: flex; flex-direction: column; gap: 4px;">
    <div style="display: flex; justify-content: space-between; align-items: baseline; gap: 10px;">
      <span style="font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--ink-muted); letter-spacing: 0.1em;">${escapeHtml(toCur)}</span>
      <span style="font-family: 'Outfit', sans-serif; font-size: 28px; font-weight: 600; color: var(--ink); font-variant-numeric: tabular-nums;">${escapeHtml(Number(total).toLocaleString(undefined, { maximumFractionDigits: 0 }))}</span>
    </div>
    ${missing > 0 ? `<div style="font-size: 10px; color: var(--warn);">${missing} deal${missing===1?'':'s'} excluded (rate missing)</div>` : ''}
  </div>`;
}
// Render a small one-line currency summary that respects the dashboard
// display-currency toggle. Used in compact spots (status panel, company list).
function renderStatusTotal(deals) {
  const dc = state.settings.displayCurrency;
  if (dc !== 'native' && fxRatesAvailable()) {
    const { total, missing } = sumInCurrency(deals, dc);
    return fmtMoney(total, dc) + (missing ? ` (+${missing} excl)` : '');
  }
  return renderCurrencyTotals(sumByCurrency(deals));
}
// Either stacked-native or converted, depending on settings.displayCurrency
function renderTotalOrStack(buckets, deals, valueFn) {
  const dc = state.settings.displayCurrency;
  if (dc === 'native' || !fxRatesAvailable()) {
    return renderCurrencyStack(buckets);
  }
  return renderConvertedTotal(deals, dc, valueFn);
}

registerView('dashboard', () => {
  const activeOpenDeals = state.deals.filter(d => d.stage !== 'won' && d.stage !== 'lost' && d.status === 'active');
  const allOpenDeals = state.deals.filter(d => d.stage !== 'won' && d.stage !== 'lost');
  const wonDeals  = state.deals.filter(d => d.stage === 'won');
  const blockedDeals = state.deals.filter(d => d.status === 'blocked');
  const stoppedDeals = state.deals.filter(d => d.status === 'stopped');
  const cancelledDeals = state.deals.filter(d => d.status === 'cancelled');

  // Per-currency totals (only count active-status deals in the forecast)
  const pipelineTotals = sumByCurrency(activeOpenDeals);
  const weightedTotals = sumByCurrency(activeOpenDeals, (d) => {
    const stg = state.settings.dealStages.find(st => st.id === d.stage);
    return (Number(d.value)||0) * (stg ? stg.probability/100 : 0);
  });
  const wonTotals = sumByCurrency(wonDeals);

  const openTickets = state.tickets.filter(t => t.stage !== 'closed').length;
  const upcoming = state.activities.filter(a => !a.done && a.date).sort((a,b) => a.date.localeCompare(b.date)).slice(0, 6);
  const recent = [...state.activities].sort((a,b) => b.createdAt - a.createdAt).slice(0, 6);

  // Stage bars: when display currency is native, use workspace currency.
  // When converted, sum everything into the converted currency.
  const wsCur = state.settings.currency;
  const dc = state.settings.displayCurrency;
  const stageBarMode = (dc !== 'native' && fxRatesAvailable()) ? 'converted' : 'native';
  const stageBars = state.settings.dealStages.map(s => {
    let deals, val;
    if (stageBarMode === 'converted') {
      deals = state.deals.filter(d => d.stage === s.id && d.status === 'active');
      val = sumInCurrency(deals, dc).total;
    } else {
      deals = state.deals.filter(d => d.stage === s.id && (d.currency || wsCur) === wsCur && d.status === 'active');
      val = deals.reduce((a,d) => a + (Number(d.value)||0), 0);
    }
    return { ...s, count: deals.length, value: val };
  });
  const stageBarCur = stageBarMode === 'converted' ? dc : wsCur;
  const maxStage = Math.max(1, ...stageBars.map(t => t.value));

  const topDeals = [...activeOpenDeals].sort((a,b) => (Number(b.value)||0) - (Number(a.value)||0)).slice(0, 4);

  // Display-currency toggle pill
  const dcOptions = ['native', 'ZAR', 'USD', 'EUR'];
  const fxAvail = fxRatesAvailable();
  const fxCaption = fxAvail
    ? `1 USD = ${state.fxRates.zarPerUsd} ZAR · 1 EUR = ${state.fxRates.zarPerEur} ZAR${state.fxRates.asOfDate ? ' · as of ' + state.fxRates.asOfDate : ''}`
    : 'FX rates not set — admin can configure them in Settings';

  return `
    <header class="page-head">
      <div>
        <h1 class="page-title">Dash<span class="accent">board</span></h1>
        <div class="page-sub">${todayStr()} · ${state.companies.length} companies · ${state.contacts.length} contacts · default currency ${escapeHtml(wsCur)}</div>
      </div>
      <div style="display:flex; gap:10px;">
        <button class="btn" onclick="window._crm.quickAction('activity')">+ Log activity</button>
        <button class="btn primary" onclick="window._crm.quickAction('deal')">+ New deal</button>
      </div>
    </header>

    <div class="filter-bar" style="margin-bottom: 14px; align-items: center;">
      <span class="inline-note">Show totals as:</span>
      ${dcOptions.map(opt => {
        const label = opt === 'native' ? 'Native (per currency)' : opt;
        const disabled = opt !== 'native' && !fxAvail;
        return `<button class="btn small ${dc===opt?'primary':'ghost'}" ${disabled?'disabled title="Set FX rates in Settings to enable"':''} onclick="window._crm.setDisplayCurrency('${opt}')">${escapeHtml(label)}</button>`;
      }).join('')}
      <span class="inline-note" style="margin-left: 12px;">${escapeHtml(fxCaption)}</span>
    </div>

    <div class="stat-grid">
      <div class="stat hi">
        <div class="k">Open pipeline</div>
        <div class="v" style="margin: 6px 0;">${renderTotalOrStack(pipelineTotals, activeOpenDeals)}</div>
        <div class="d">${activeOpenDeals.length} active deals${blockedDeals.length ? ' · ' + blockedDeals.length + ' blocked' : ''}</div>
      </div>
      <div class="stat">
        <div class="k">Weighted forecast</div>
        <div class="v" style="margin: 6px 0;">${renderTotalOrStack(weightedTotals, activeOpenDeals, (d) => {
          const stg = state.settings.dealStages.find(st => st.id === d.stage);
          return (Number(d.value)||0) * (stg ? stg.probability/100 : 0);
        })}</div>
        <div class="d">by stage probability</div>
      </div>
      <div class="stat">
        <div class="k">Won (all time)</div>
        <div class="v" style="margin: 6px 0;">${renderTotalOrStack(wonTotals, wonDeals)}</div>
        <div class="d">${wonDeals.length} closed deals</div>
      </div>
      <div class="stat">
        <div class="k">Active tickets</div>
        <div class="v">${openTickets}</div>
        <div class="d">${state.tickets.filter(t => t.priority === 'high' && t.stage !== 'closed').length} high priority</div>
      </div>
    </div>

    ${(blockedDeals.length || stoppedDeals.length || cancelledDeals.length) ? `
      <div class="panel" style="margin-bottom:18px;">
        <h3>Status flags</h3>
        <div style="display:grid; grid-template-columns: repeat(3, 1fr); gap: 14px;">
          <div style="padding: 12px 14px; background: var(--bg-soft); border-left: 3px solid var(--warn); border-radius: 3px;">
            <div class="inline-note" style="color: var(--warn);">Blocked</div>
            <div style="font-family:'Outfit',sans-serif; font-size: 22px; font-weight:600; color: var(--ink); margin-top: 4px;">${blockedDeals.length}</div>
            <div style="font-size: 11px; color: var(--ink-muted); font-family: 'JetBrains Mono',monospace;">${escapeHtml(renderStatusTotal(blockedDeals))}</div>
          </div>
          <div style="padding: 12px 14px; background: var(--bg-soft); border-left: 3px solid var(--ink-muted); border-radius: 3px;">
            <div class="inline-note">Stopped</div>
            <div style="font-family:'Outfit',sans-serif; font-size: 22px; font-weight:600; color: var(--ink); margin-top: 4px;">${stoppedDeals.length}</div>
            <div style="font-size: 11px; color: var(--ink-muted); font-family: 'JetBrains Mono',monospace;">${escapeHtml(renderStatusTotal(stoppedDeals))}</div>
          </div>
          <div style="padding: 12px 14px; background: var(--bg-soft); border-left: 3px solid var(--bad); border-radius: 3px;">
            <div class="inline-note" style="color: var(--bad);">Cancelled</div>
            <div style="font-family:'Outfit',sans-serif; font-size: 22px; font-weight:600; color: var(--ink); margin-top: 4px;">${cancelledDeals.length}</div>
            <div style="font-size: 11px; color: var(--ink-muted); font-family: 'JetBrains Mono',monospace;">${escapeHtml(renderStatusTotal(cancelledDeals))}</div>
          </div>
        </div>
      </div>
    ` : ''}

    <div class="dash-grid">
      <div class="panel">
        <h3>Pipeline by stage <a class="act" onclick="window._crm.navigate('pipeline')">Open board →</a></h3>
        <div class="inline-note" style="margin-bottom: 8px;">${stageBarMode === 'converted' ? 'All deals converted to ' + escapeHtml(dc) : 'Active ' + escapeHtml(wsCur) + ' deals only'}</div>
        ${stageBars.map(t => `
          <div style="display:grid; grid-template-columns: 90px 1fr auto; gap: 12px; align-items: center; margin: 8px 0; font-size: 13px;">
            <div class="inline-note">${escapeHtml(t.name)}</div>
            <div style="height: 18px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 3px; overflow: hidden;">
              <div style="height:100%; width: ${(t.value/maxStage*100)}%; background: linear-gradient(90deg, var(--brand-blue), var(--accent)); transition: width 0.3s;"></div>
            </div>
            <div style="font-family:'JetBrains Mono',monospace; font-size: 11px; color: var(--ink-dim); min-width: 130px; text-align: right;">
              ${t.count} · ${escapeHtml(fmtMoney(t.value, stageBarCur))}
            </div>
          </div>
        `).join('')}
        <div class="hrule"></div>
        <h3>Recent activity <a class="act" onclick="window._crm.navigate('activities')">View all →</a></h3>
        ${recent.length ? '<ul style="list-style:none; padding:0; margin:0;">' + recent.map(a => renderActivityRowMini(a)).join('') + '</ul>'
          : '<div class="empty">No activity yet — log your first call, email, or meeting.</div>'}
      </div>
      <div class="panel">
        <h3>My tasks <a class="act" onclick="window._crm.navigate('tasks')">View all →</a></h3>
        ${upcoming.length ? '<ul style="list-style:none; padding:0; margin:0;">' + upcoming.map(a => renderActivityRowMini(a, true)).join('') + '</ul>'
          : '<div class="empty">Nothing scheduled.</div>'}
        <div class="hrule"></div>
        <h3>Top deals <a class="act" onclick="window._crm.navigate('deals')">All deals →</a></h3>
        ${topDeals.length ? topDeals.map(d => {
          const co = getCompany(d.companyId);
          return `<div style="padding: 11px 0; border-bottom: 1px dashed var(--border); cursor:pointer;" onclick="window._crm.navigate('deal-detail', { id: '${d.id}' })">
            <div style="display:flex; justify-content:space-between; gap: 10px;">
              <div style="font-weight:600; font-size: 13.5px; color: var(--ink);">${escapeHtml(d.title)}</div>
              <div style="font-family:'JetBrains Mono',monospace; font-size: 12px; color: var(--accent); font-weight: 700;">${escapeHtml(fmtDealMoney(d))}</div>
            </div>
            <div class="inline-note" style="margin-top:3px;">${escapeHtml(co ? co.name : '—')} · ${escapeHtml(dealStageName(d.stage))}</div>
          </div>`;
        }).join('') : '<div class="empty">No open deals.</div>'}
      </div>
    </div>
  `;
});

// =============================================================
// TASKS
// =============================================================
function renderTasksRows(list) {
  if (!list.length) return '<tr><td colspan="5"><div class="empty">No tasks.</div></td></tr>';
  const sorted = [...list].sort((a,b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    return (a.date||'').localeCompare(b.date||'');
  });
  return sorted.map(a => {
    const c = getContact(a.contactId);
    const d = getDeal(a.dealId);
    const t = a.ticketId ? getTicket(a.ticketId) : null;
    const overdue = !a.done && a.date && a.date < todayStr();
    const linked = [c?contactName(c):null, d?d.title:null, t?t.subject:null].filter(Boolean).join(' · ') || '—';
    return `<tr onclick="window._crm.editActivity('${a.id}')">
      <td onclick="event.stopPropagation(); window._crm.toggleActivity('${a.id}')" style="cursor:pointer; text-align:center;">
        <input type="checkbox" ${a.done ? 'checked' : ''} onclick="event.stopPropagation(); window._crm.toggleActivity('${a.id}')" />
      </td>
      <td style="${a.done?'text-decoration:line-through; color:var(--ink-muted);':'color: var(--ink);'} font-weight:600;">${escapeHtml(a.subject)}</td>
      <td>${escapeHtml(linked)}</td>
      <td style="${overdue?'color:var(--bad); font-weight:600;':''}">${escapeHtml(fmtDate(a.date))} ${overdue?'<span class="inline-note">· overdue</span>':''}</td>
      <td class="row-actions" onclick="event.stopPropagation()"><button class="btn small" onclick="window._crm.editActivity('${a.id}')">Edit</button></td>
    </tr>`;
  }).join('');
}
registerView('tasks', () => {
  const all = state.activities.filter(a => a.type === 'task');
  return `
    <header class="page-head">
      <div>
        <h1 class="page-title">Ta<span class="accent">sks</span></h1>
        <div class="page-sub">${all.filter(a=>!a.done).length} open · ${all.filter(a=>a.done).length} completed</div>
      </div>
      <button class="btn primary" onclick="window._crm.editActivity(null, { type: 'task' })">+ New task</button>
    </header>
    <div class="filter-bar">
      <input type="text" class="search" id="tk-search" placeholder="Search tasks…" />
      <select id="tk-filter">
        <option value="open">Open</option>
        <option value="today">Due today</option>
        <option value="overdue">Overdue</option>
        <option value="done">Completed</option>
        <option value="all">All</option>
      </select>
    </div>
    <div class="table-wrap"><div class="table-scroll">
      <table class="data">
        <thead><tr><th style="width:40px;"></th><th>Subject</th><th>Linked to</th><th>Due</th><th></th></tr></thead>
        <tbody id="tk-tbody">${renderTasksRows(all)}</tbody>
      </table>
    </div></div>
  `;
}, () => {
  const s = document.getElementById('tk-search');
  const f = document.getElementById('tk-filter');
  const apply = () => {
    const q = (s.value||'').toLowerCase();
    const fv = f.value;
    let filtered = state.activities.filter(a => a.type === 'task');
    if (fv === 'open') filtered = filtered.filter(a => !a.done);
    else if (fv === 'done') filtered = filtered.filter(a => a.done);
    else if (fv === 'overdue') filtered = filtered.filter(a => !a.done && a.date && a.date < todayStr());
    else if (fv === 'today') filtered = filtered.filter(a => a.date === todayStr());
    if (q) filtered = filtered.filter(a => ((a.subject||'') + ' ' + (a.notes||'')).toLowerCase().includes(q));
    document.getElementById('tk-tbody').innerHTML = renderTasksRows(filtered);
  };
  s.addEventListener('input', apply);
  f.addEventListener('change', apply);
});

// =============================================================
// PIPELINE (deals kanban)
// =============================================================
registerView('pipeline', () => {
  const stages = state.settings.dealStages;
  // Pipeline shows ALL deals in workspace currency, status filterable in UI.
  // We display blocked/stopped/cancelled with a badge so they're visible but visually distinct.
  const wsCur = state.settings.currency;
  const visibleDeals = state.deals.filter(d => (d.currency || wsCur) === wsCur);

  return `
    <header class="page-head">
      <div>
        <h1 class="page-title">Deal pipe<span class="accent">line</span></h1>
        <div class="page-sub">Drag cards across stages · ${state.deals.length} deals · showing ${escapeHtml(wsCur)}</div>
      </div>
      <button class="btn primary" onclick="window._crm.editDeal(null)">+ New deal</button>
    </header>

    ${state.settings.currencies.length > 1 ? `
      <div class="filter-bar" style="margin-bottom: 14px;">
        <span class="inline-note">Currency:</span>
        ${state.settings.currencies.map(cur => `
          <button class="btn small ${cur === wsCur ? 'primary' : 'ghost'}" onclick="window._crm.setWorkspaceCurrency('${cur}')">${cur}</button>
        `).join('')}
        <span class="inline-note" style="margin-left: 12px;">(switching only changes which deals are shown here, not the deal data)</span>
      </div>
    ` : ''}

    <div class="pipeline">
      ${stages.map(st => {
        const dealsInStage = visibleDeals.filter(d => d.stage === st.id);
        // Active-only total (the honest forecast number)
        const activeTotal = dealsInStage.filter(d => d.status === 'active').reduce((a,d) => a + (Number(d.value)||0), 0);
        const blockedCt = dealsInStage.filter(d => d.status === 'blocked').length;
        return `
          <div class="stage">
            <div class="stage-head">
              <div class="name">${escapeHtml(st.name)} · ${st.probability}%</div>
              <div class="totals"><div class="count">${dealsInStage.length}</div><div class="val">${escapeHtml(fmtMoney(activeTotal, wsCur))}</div></div>
              ${blockedCt ? `<div class="inline-note" style="color: var(--warn); margin-top: 4px;">${blockedCt} blocked</div>` : ''}
            </div>
            <div class="stage-body" data-stage="${st.id}">
              ${dealsInStage.map(d => {
                const co = getCompany(d.companyId);
                const prio = d.priority === 'high' ? 'prio-high' : d.priority === 'medium' ? 'prio-med' : d.priority === 'low' ? 'prio-low' : '';
                const stat = dealStatus(d.status);
                const statClass = d.status !== 'active' ? `status-${d.status}` : '';
                const statBadge = d.status !== 'active' ? `<span class="chip ${stat.color}" style="font-size: 9px; padding: 2px 6px; margin-left: 4px;">${escapeHtml(stat.name)}</span>` : '';
                return `<div class="deal-card ${prio} ${statClass}" draggable="true" data-deal="${d.id}" onclick="window._crm.navigate('deal-detail', { id: '${d.id}' })">
                  <div class="name">${escapeHtml(d.title)}${statBadge}</div>
                  <div class="meta"><span>${escapeHtml(co ? co.name : '—')}</span><span class="value">${escapeHtml(fmtDealMoney(d))}</span></div>
                </div>`;
              }).join('')}
            </div>
          </div>`;
      }).join('')}
    </div>
  `;
}, () => {
  setupKanbanDragDrop('.deal-card', 'deal', async (id, stageId) => {
    const deal = getDeal(id);
    if (deal && deal.stage !== stageId) {
      await runAsync(() => store.deals.updateStage(id, stageId));
      toast('Deal → ' + dealStageName(stageId));
      render();
    }
  });
});

// =============================================================
// TICKETS BOARD
// =============================================================
registerView('tickets-board', () => {
  const stages = state.settings.ticketStages;
  return `
    <header class="page-head">
      <div>
        <h1 class="page-title">Ticket <span class="accent">board</span></h1>
        <div class="page-sub">Drag tickets across stages · ${state.tickets.length} total</div>
      </div>
      <button class="btn primary" onclick="window._crm.editTicket(null)">+ New ticket</button>
    </header>
    <div class="pipeline service">
      ${stages.map(st => {
        const items = state.tickets.filter(t => t.stage === st.id);
        return `
          <div class="stage">
            <div class="stage-head">
              <div class="name">${escapeHtml(st.name)}</div>
              <div class="totals"><div class="count">${items.length}</div><div class="val">${items.filter(t => t.priority==='high').length} high</div></div>
            </div>
            <div class="stage-body" data-stage="${st.id}">
              ${items.map(t => {
                const co = getCompany(t.companyId);
                const prio = t.priority === 'high' ? 'prio-high' : t.priority === 'medium' ? 'prio-med' : t.priority === 'low' ? 'prio-low' : '';
                return `<div class="deal-card ${prio}" draggable="true" data-ticket="${t.id}" onclick="window._crm.navigate('ticket-detail', { id: '${t.id}' })">
                  <div class="name">${escapeHtml(t.subject)}</div>
                  <div class="meta"><span>${escapeHtml(co ? co.name : '—')}</span><span>${escapeHtml((t.priority||'—').toUpperCase())}</span></div>
                </div>`;
              }).join('')}
            </div>
          </div>`;
      }).join('')}
    </div>
  `;
}, () => {
  setupKanbanDragDrop('.deal-card', 'ticket', async (id, stageId) => {
    const t = getTicket(id);
    if (t && t.stage !== stageId) {
      await runAsync(() => store.tickets.updateStage(id, stageId));
      toast('Ticket → ' + ticketStageName(stageId));
      render();
    }
  });
});

// =============================================================
// COMPANIES LIST
// =============================================================
function renderCompanyRows(list) {
  if (!list.length) return '<tr><td colspan="7"><div class="empty">No companies match.</div></td></tr>';
  return list.map(co => {
    const contacts = state.contacts.filter(c => c.companyId === co.id).length;
    const openDeals = state.deals.filter(d => d.companyId === co.id && d.stage !== 'won' && d.stage !== 'lost' && d.status === 'active');
    const pipeBuckets = sumByCurrency(openDeals);
    const pipeLabel = renderCurrencyTotals(pipeBuckets, { zero: '—' });
    return `<tr onclick="window._crm.navigate('company-detail', { id: '${co.id}' })">
      <td><div style="font-weight:600; color: var(--ink);">${escapeHtml(co.name)}</div><div class="inline-note" style="margin-top:3px;">${escapeHtml(co.website || '')}</div></td>
      <td>${escapeHtml(co.industry || '—')}</td>
      <td><span class="chip">${escapeHtml(co.segment || '—')}</span></td>
      <td>${contacts}</td>
      <td>${openDeals.length}</td>
      <td style="font-family:'JetBrains Mono',monospace; color: var(--ink);">${escapeHtml(pipeLabel)}</td>
      <td class="row-actions" onclick="event.stopPropagation()"><button class="btn small" onclick="window._crm.editCompany('${co.id}')">Edit</button></td>
    </tr>`;
  }).join('');
}
registerView('companies', () => `
  <header class="page-head">
    <div>
      <h1 class="page-title">Comp<span class="accent">anies</span></h1>
      <div class="page-sub">${state.companies.length} records</div>
    </div>
    <button class="btn primary" onclick="window._crm.editCompany(null)">+ New company</button>
  </header>
  <div class="filter-bar">
    <input type="text" class="search" id="co-search" placeholder="Search by name, industry, segment…" />
    <select id="co-segment">
      <option value="">All segments</option>
      ${state.settings.segments.map(s => `<option value="${escapeHtml(s)}">${escapeHtml(s)}</option>`).join('')}
    </select>
  </div>
  <div class="table-wrap"><div class="table-scroll">
    <table class="data">
      <thead><tr><th>Company</th><th>Industry</th><th>Segment</th><th>Contacts</th><th>Open deals</th><th>Pipeline</th><th></th></tr></thead>
      <tbody id="co-tbody">${renderCompanyRows(state.companies)}</tbody>
    </table>
  </div></div>
`, () => {
  const s = document.getElementById('co-search');
  const seg = document.getElementById('co-segment');
  const apply = () => {
    const q = (s.value || '').toLowerCase();
    const sv = seg.value;
    const filtered = state.companies.filter(c => {
      if (sv && c.segment !== sv) return false;
      if (!q) return true;
      return (c.name + ' ' + (c.industry||'') + ' ' + (c.segment||'') + ' ' + (c.notes||'')).toLowerCase().includes(q);
    });
    document.getElementById('co-tbody').innerHTML = renderCompanyRows(filtered);
  };
  s.addEventListener('input', apply);
  seg.addEventListener('change', apply);
});

// =============================================================
// COMPANY DETAIL
// =============================================================
registerView('company-detail', (params) => {
  const co = getCompany(params && params.id);
  if (!co) return '<div class="empty">Company not found.</div>';
  const contacts = state.contacts.filter(c => c.companyId === co.id);
  const deals = state.deals.filter(d => d.companyId === co.id);
  const tickets = state.tickets.filter(t => t.companyId === co.id);
  const activities = state.activities.filter(a => {
    if (a.companyId === co.id) return true;
    if (a.dealId && deals.some(d => d.id === a.dealId)) return true;
    if (a.contactId && contacts.some(c => c.id === a.contactId)) return true;
    return false;
  });
  return `
    <div class="crumbs"><a onclick="window._crm.navigate('companies')">Companies</a> / ${escapeHtml(co.name)}</div>
    <header class="page-head">
      <div>
        <h1 class="page-title">${escapeHtml(co.name)}</h1>
        <div class="page-sub">${escapeHtml(co.industry || 'Company')}${co.segment ? ' · ' + escapeHtml(co.segment) : ''}</div>
      </div>
      <div style="display:flex; gap:8px;">
        <button class="btn" onclick="window._crm.editCompany('${co.id}')">Edit</button>
        <button class="btn primary" onclick="window._crm.editDeal(null, { companyId: '${co.id}' })">+ Deal</button>
      </div>
    </header>
    <div class="record-layout">
      <div class="record-side">
        <div class="record-avatar">${escapeHtml(initials(co.name))}</div>
        <h2 class="record-name">${escapeHtml(co.name)}</h2>
        <div class="record-role">${escapeHtml(co.industry || '')}${co.segment ? ' · ' + escapeHtml(co.segment) : ''}</div>
        <div class="record-actions">
          <button class="btn small" onclick="window._crm.editCompany('${co.id}')">Edit</button>
          <button class="btn small" onclick="window._crm.editContact(null, { companyId: '${co.id}' })">+ Contact</button>
          <button class="btn small" onclick="window._crm.editTicket(null, { companyId: '${co.id}' })">+ Ticket</button>
        </div>
        <div class="record-props">
          <div class="record-prop"><div class="k">Industry</div><div class="v">${escapeHtml(co.industry || '—')}</div></div>
          <div class="record-prop"><div class="k">Segment</div><div class="v">${co.segment ? '<span class="chip">' + escapeHtml(co.segment) + '</span>' : '—'}</div></div>
          <div class="record-prop"><div class="k">Website</div><div class="v">${escapeHtml(co.website || '—')}</div></div>
          <div class="record-prop"><div class="k">Phone</div><div class="v">${escapeHtml(co.phone || '—')}</div></div>
          <div class="record-prop"><div class="k">Address</div><div class="v">${escapeHtml(co.address || '—')}</div></div>
          <div class="record-prop"><div class="k">Notes</div><div class="v" style="white-space:pre-wrap;">${escapeHtml(co.notes || '—')}</div></div>
        </div>
      </div>
      <div class="record-mid">
        ${renderQuickNote({ companyId: co.id })}
        ${renderTimeline(activities, { title: 'Activity timeline' })}
      </div>
      <div class="record-right">
        <div class="rel-panel" style="margin-bottom:14px;">
          <h4>Contacts (${contacts.length}) <a onclick="window._crm.editContact(null, { companyId: '${co.id}' })">+ Add</a></h4>
          ${contacts.length ? contacts.map(c => `<div class="rel-item" onclick="window._crm.navigate('contact-detail', { id: '${c.id}' })"><div class="t">${escapeHtml(contactName(c))}</div><div class="s">${escapeHtml(c.title || '')}</div></div>`).join('') : '<div class="inline-note">None yet.</div>'}
        </div>
        <div class="rel-panel" style="margin-bottom:14px;">
          <h4>Deals (${deals.length}) <a onclick="window._crm.editDeal(null, { companyId: '${co.id}' })">+ Add</a></h4>
          ${deals.length ? deals.map(d => {
            const stat = dealStatus(d.status);
            const statBadge = d.status !== 'active' ? ` <span class="chip ${stat.color}" style="font-size:9px; padding:1px 5px;">${escapeHtml(stat.name)}</span>` : '';
            return `<div class="rel-item" onclick="window._crm.navigate('deal-detail', { id: '${d.id}' })"><div class="t">${escapeHtml(d.title)}${statBadge}</div><div class="s">${escapeHtml(dealStageName(d.stage))} · ${escapeHtml(fmtDealMoney(d))}</div></div>`;
          }).join('') : '<div class="inline-note">None yet.</div>'}
        </div>
        <div style="margin-bottom:14px;">${renderAttachmentsPanel('company', co.id)}</div>
        <div class="rel-panel">
          <h4>Tickets (${tickets.length}) <a onclick="window._crm.editTicket(null, { companyId: '${co.id}' })">+ Add</a></h4>
          ${tickets.length ? tickets.map(t => `<div class="rel-item" onclick="window._crm.navigate('ticket-detail', { id: '${t.id}' })"><div class="t">${escapeHtml(t.subject)}</div><div class="s">${escapeHtml(ticketStageName(t.stage))}${t.priority ? ' · ' + escapeHtml(t.priority.toUpperCase()) : ''}</div></div>`).join('') : '<div class="inline-note">None yet.</div>'}
        </div>
      </div>
    </div>
  `;
}, (params) => {
  if (!params || !getCompany(params.id)) return;
  setupQuickNote();
  setupTimelineFilters();
});

// =============================================================
// CONTACTS LIST
// =============================================================
function renderContactRows(list) {
  if (!list.length) return '<tr><td colspan="6"><div class="empty">No contacts.</div></td></tr>';
  return list.map(c => {
    const co = getCompany(c.companyId);
    return `<tr onclick="window._crm.navigate('contact-detail', { id: '${c.id}' })">
      <td style="font-weight:600; color:var(--ink);">${escapeHtml(contactName(c))}</td>
      <td>${escapeHtml(c.title||'—')}</td>
      <td>${escapeHtml(co?co.name:'—')}</td>
      <td>${escapeHtml(c.email||'—')}</td>
      <td>${escapeHtml(c.phone||'—')}</td>
      <td class="row-actions" onclick="event.stopPropagation()"><button class="btn small" onclick="window._crm.editContact('${c.id}')">Edit</button></td>
    </tr>`;
  }).join('');
}
registerView('contacts', () => `
  <header class="page-head">
    <div><h1 class="page-title">Con<span class="accent">tacts</span></h1><div class="page-sub">${state.contacts.length} records</div></div>
    <button class="btn primary" onclick="window._crm.editContact(null)">+ New contact</button>
  </header>
  <div class="filter-bar">
    <input type="text" class="search" id="ct-search" placeholder="Search name, email, company…" />
    <select id="ct-company">
      <option value="">All companies</option>
      ${state.companies.map(c => `<option value="${c.id}">${escapeHtml(c.name)}</option>`).join('')}
    </select>
  </div>
  <div class="table-wrap"><div class="table-scroll">
    <table class="data">
      <thead><tr><th>Name</th><th>Title</th><th>Company</th><th>Email</th><th>Phone</th><th></th></tr></thead>
      <tbody id="ct-tbody">${renderContactRows(state.contacts)}</tbody>
    </table>
  </div></div>
`, () => {
  const s = document.getElementById('ct-search');
  const co = document.getElementById('ct-company');
  const apply = () => {
    const q = (s.value||'').toLowerCase();
    const cid = co.value;
    const filtered = state.contacts.filter(c => {
      if (cid && c.companyId !== cid) return false;
      if (!q) return true;
      const company = getCompany(c.companyId);
      return (contactName(c) + ' ' + (c.email||'') + ' ' + (c.title||'') + ' ' + (company?company.name:'')).toLowerCase().includes(q);
    });
    document.getElementById('ct-tbody').innerHTML = renderContactRows(filtered);
  };
  s.addEventListener('input', apply);
  co.addEventListener('change', apply);
});

// =============================================================
// CONTACT DETAIL
// =============================================================
registerView('contact-detail', (params) => {
  const c = getContact(params && params.id);
  if (!c) return '<div class="empty">Contact not found.</div>';
  const co = getCompany(c.companyId);
  const deals = state.deals.filter(d => d.contactId === c.id);
  const tickets = state.tickets.filter(t => t.contactId === c.id);
  const activities = state.activities.filter(a => a.contactId === c.id);
  return `
    <div class="crumbs"><a onclick="window._crm.navigate('contacts')">Contacts</a> / ${escapeHtml(contactName(c))}</div>
    <header class="page-head">
      <div>
        <h1 class="page-title">${escapeHtml(contactName(c))}</h1>
        <div class="page-sub">${escapeHtml(c.title || 'Contact')}${co ? ' · ' + escapeHtml(co.name) : ''}</div>
      </div>
      <div style="display:flex; gap:8px;">
        <button class="btn" onclick="window._crm.editContact('${c.id}')">Edit</button>
        <button class="btn primary" onclick="window._crm.editDeal(null, { contactId: '${c.id}', companyId: '${c.companyId||''}' })">+ Deal</button>
      </div>
    </header>
    <div class="record-layout">
      <div class="record-side">
        <div class="record-avatar">${escapeHtml(initials(contactName(c)))}</div>
        <h2 class="record-name">${escapeHtml(contactName(c))}</h2>
        <div class="record-role">${escapeHtml(c.title || '')}${co ? ' · ' + escapeHtml(co.name) : ''}</div>
        <div class="record-actions">
          <button class="btn small" onclick="window._crm.editContact('${c.id}')">Edit</button>
          ${c.email ? `<button class="btn small" onclick="window.location.href='mailto:${escapeHtml(c.email)}'">Email</button>` : ''}
        </div>
        <div class="record-props">
          <div class="record-prop"><div class="k">Email</div><div class="v">${c.email ? '<a href="mailto:' + escapeHtml(c.email) + '">' + escapeHtml(c.email) + '</a>' : '—'}</div></div>
          <div class="record-prop"><div class="k">Phone</div><div class="v">${escapeHtml(c.phone || '—')}</div></div>
          <div class="record-prop"><div class="k">Company</div><div class="v">${co ? '<a onclick="window._crm.navigate(\'company-detail\', { id: \'' + co.id + '\' })">' + escapeHtml(co.name) + '</a>' : '—'}</div></div>
          <div class="record-prop"><div class="k">Title</div><div class="v">${escapeHtml(c.title || '—')}</div></div>
          <div class="record-prop"><div class="k">Lifecycle</div><div class="v">${c.lifecycle ? '<span class="chip">' + escapeHtml(c.lifecycle) + '</span>' : '—'}</div></div>
          <div class="record-prop"><div class="k">Notes</div><div class="v" style="white-space:pre-wrap;">${escapeHtml(c.notes || '—')}</div></div>
        </div>
      </div>
      <div class="record-mid">
        ${renderQuickNote({ contactId: c.id, companyId: c.companyId })}
        ${renderTimeline(activities, { title: 'Activity timeline' })}
      </div>
      <div class="record-right">
        <div class="rel-panel" style="margin-bottom:14px;">
          <h4>Deals (${deals.length}) <a onclick="window._crm.editDeal(null, { contactId: '${c.id}', companyId: '${c.companyId||''}' })">+ Add</a></h4>
          ${deals.length ? deals.map(d => {
            const stat = dealStatus(d.status);
            const statBadge = d.status !== 'active' ? ` <span class="chip ${stat.color}" style="font-size:9px; padding:1px 5px;">${escapeHtml(stat.name)}</span>` : '';
            return `<div class="rel-item" onclick="window._crm.navigate('deal-detail', { id: '${d.id}' })"><div class="t">${escapeHtml(d.title)}${statBadge}</div><div class="s">${escapeHtml(dealStageName(d.stage))} · ${escapeHtml(fmtDealMoney(d))}</div></div>`;
          }).join('') : '<div class="inline-note">None yet.</div>'}
        </div>
        <div class="rel-panel">
          <h4>Tickets (${tickets.length}) <a onclick="window._crm.editTicket(null, { contactId: '${c.id}', companyId: '${c.companyId||''}' })">+ Add</a></h4>
          ${tickets.length ? tickets.map(t => `<div class="rel-item" onclick="window._crm.navigate('ticket-detail', { id: '${t.id}' })"><div class="t">${escapeHtml(t.subject)}</div><div class="s">${escapeHtml(ticketStageName(t.stage))}</div></div>`).join('') : '<div class="inline-note">None yet.</div>'}
        </div>
      </div>
    </div>
  `;
}, (params) => {
  if (!params || !getContact(params.id)) return;
  setupQuickNote();
  setupTimelineFilters();
});

// =============================================================
// DEALS LIST
// =============================================================
function renderDealRows(list) {
  if (!list.length) return '<tr><td colspan="8"><div class="empty">No deals.</div></td></tr>';
  return list.map(d => {
    const co = getCompany(d.companyId);
    const c = getContact(d.contactId);
    const chip = d.stage === 'won' ? 'ok' : d.stage === 'lost' ? 'bad' : d.stage === 'proposal' ? 'accent' : '';
    const stat = dealStatus(d.status);
    const statCell = d.status !== 'active' ? `<span class="chip ${stat.color}">${escapeHtml(stat.name)}</span>` : '<span class="inline-note">—</span>';
    return `<tr onclick="window._crm.navigate('deal-detail', { id: '${d.id}' })">
      <td style="font-weight:600; color:var(--ink);">${escapeHtml(d.title)}${d.quoteNumber ? '<div class="inline-note" style="margin-top:3px;">Quote: ' + escapeHtml(d.quoteNumber) + '</div>' : ''}</td>
      <td>${escapeHtml(co?co.name:'—')}</td>
      <td>${escapeHtml(c?contactName(c):'—')}</td>
      <td><span class="chip ${chip}">${escapeHtml(d.stage==='lost'?'Lost':dealStageName(d.stage))}</span></td>
      <td>${statCell}</td>
      <td style="font-family:'JetBrains Mono',monospace; color:var(--ink);">${escapeHtml(fmtDealMoney(d))}</td>
      <td>${escapeHtml(fmtDate(d.expectedClose))}</td>
      <td class="row-actions" onclick="event.stopPropagation()"><button class="btn small" onclick="window._crm.editDeal('${d.id}')">Edit</button></td>
    </tr>`;
  }).join('');
}
registerView('deals', () => `
  <header class="page-head">
    <div><h1 class="page-title">Deals<span class="accent">.</span></h1><div class="page-sub">${state.deals.length} records · ${state.deals.filter(d => d.stage !== 'won' && d.stage !== 'lost').length} open · ${state.deals.filter(d => d.status !== 'active').length} flagged</div></div>
    <button class="btn primary" onclick="window._crm.editDeal(null)">+ New deal</button>
  </header>
  <div class="filter-bar">
    <input type="text" class="search" id="d-search" placeholder="Search deals, quote numbers…" />
    <select id="d-stage">
      <option value="">All stages</option>
      ${state.settings.dealStages.map(s => `<option value="${s.id}">${escapeHtml(s.name)}</option>`).join('')}
      <option value="lost">Lost</option>
    </select>
    <select id="d-status">
      <option value="">Any status</option>
      ${state.settings.dealStatuses.map(s => `<option value="${s.id}">${escapeHtml(s.name)}</option>`).join('')}
    </select>
    <div class="view-toggle">
      <button class="active" onclick="window._crm.navigate('deals')">List</button>
      <button onclick="window._crm.navigate('pipeline')">Board</button>
    </div>
  </div>
  <div class="table-wrap"><div class="table-scroll">
    <table class="data">
      <thead><tr><th>Deal</th><th>Company</th><th>Contact</th><th>Stage</th><th>Status</th><th>Value</th><th>Close</th><th></th></tr></thead>
      <tbody id="d-tbody">${renderDealRows(state.deals)}</tbody>
    </table>
  </div></div>
`, () => {
  const s = document.getElementById('d-search');
  const st = document.getElementById('d-stage');
  const status = document.getElementById('d-status');
  const apply = () => {
    const q = (s.value||'').toLowerCase();
    const sv = st.value;
    const stv = status.value;
    const filtered = state.deals.filter(d => {
      if (sv && d.stage !== sv) return false;
      if (stv && (d.status || 'active') !== stv) return false;
      if (!q) return true;
      const co = getCompany(d.companyId);
      return (d.title + ' ' + (co?co.name:'') + ' ' + (d.notes||'') + ' ' + (d.quoteNumber||'')).toLowerCase().includes(q);
    });
    document.getElementById('d-tbody').innerHTML = renderDealRows(filtered);
  };
  s.addEventListener('input', apply);
  st.addEventListener('change', apply);
  status.addEventListener('change', apply);
});

// =============================================================
// DEAL DETAIL
// =============================================================
registerView('deal-detail', (params) => {
  const d = getDeal(params && params.id);
  if (!d) return '<div class="empty">Deal not found.</div>';
  const co = getCompany(d.companyId);
  const c = getContact(d.contactId);
  const activities = state.activities.filter(a => a.dealId === d.id);
  const stg = state.settings.dealStages.find(s => s.id === d.stage);
  const stages = state.settings.dealStages;
  const idx = stages.findIndex(s => s.id === d.stage);
  const stat = dealStatus(d.status);
  const quoteAtt = d.quoteAttachmentId ? getAttachment(d.quoteAttachmentId) : null;

  return `
    <div class="crumbs"><a onclick="window._crm.navigate('deals')">Deals</a> / ${escapeHtml(d.title)}</div>
    <header class="page-head">
      <div>
        <h1 class="page-title">${escapeHtml(d.title)}${d.status !== 'active' ? ` <span class="chip ${stat.color}" style="font-size: 12px; vertical-align: middle; margin-left: 6px;">${escapeHtml(stat.name)}</span>` : ''}</h1>
        <div class="page-sub">${escapeHtml(dealStageName(d.stage))} · ${escapeHtml(fmtDealMoney(d))} · Close ${escapeHtml(fmtDate(d.expectedClose))}</div>
      </div>
      <div style="display:flex; gap:8px;">
        <button class="btn" onclick="window._crm.editDeal('${d.id}')">Edit</button>
        ${d.stage !== 'won' ? `<button class="btn primary" onclick="window._crm.markDealWon('${d.id}')">Mark won</button>` : ''}
      </div>
    </header>
    <div class="record-layout">
      <div class="record-side">
        <div class="record-avatar" style="background: var(--grad);">💼</div>
        <h2 class="record-name">${escapeHtml(d.title)}</h2>
        <div class="record-role">${escapeHtml(fmtDealMoney(d))}${co ? ' · ' + escapeHtml(co.name) : ''}</div>
        <div class="record-actions"><button class="btn small" onclick="window._crm.editDeal('${d.id}')">Edit</button></div>
        <div class="record-props">
          <div class="record-prop"><div class="k">Stage</div><div class="v"><span class="chip accent">${escapeHtml(dealStageName(d.stage))}</span> <span class="inline-note" style="margin-left:6px;">${stg?stg.probability:0}%</span></div></div>
          <div class="record-prop"><div class="k">Status</div><div class="v">${d.status !== 'active' ? `<span class="chip ${stat.color}">${escapeHtml(stat.name)}</span>` : '<span class="chip ok">Active</span>'}</div></div>
          <div class="record-prop"><div class="k">Value</div><div class="v" style="font-family:'JetBrains Mono',monospace; color:var(--accent); font-weight:700;">${escapeHtml(fmtDealMoney(d))}</div></div>
          <div class="record-prop"><div class="k">Currency</div><div class="v">${escapeHtml(d.currency || state.settings.currency)}</div></div>
          <div class="record-prop"><div class="k">Expected close</div><div class="v">${escapeHtml(fmtDate(d.expectedClose))}</div></div>
          <div class="record-prop"><div class="k">Priority</div><div class="v">${d.priority ? '<span class="chip">' + escapeHtml(d.priority.toUpperCase()) + '</span>' : '—'}</div></div>
          <div class="record-prop"><div class="k">Company</div><div class="v">${co ? '<a onclick="window._crm.navigate(\'company-detail\', { id: \'' + co.id + '\' })">' + escapeHtml(co.name) + '</a>' : '—'}</div></div>
          <div class="record-prop"><div class="k">Primary contact</div><div class="v">${c ? '<a onclick="window._crm.navigate(\'contact-detail\', { id: \'' + c.id + '\' })">' + escapeHtml(contactName(c)) + '</a>' : '—'}</div></div>
          <div class="record-prop"><div class="k">Notes</div><div class="v" style="white-space:pre-wrap;">${escapeHtml(d.notes || '—')}</div></div>
        </div>
      </div>
      <div class="record-mid">
        <div class="panel" style="padding:16px 20px;">
          <div class="inline-note" style="margin-bottom:10px;">Stage</div>
          <div style="display:flex; gap:4px; align-items:stretch; margin-bottom: 12px;">
            ${stages.map((s, i) => {
              const active = i <= idx;
              return `<div onclick="window._crm.setDealStage('${d.id}', '${s.id}')" style="flex:1; cursor:pointer; padding: 8px 10px; background: ${active ? 'var(--grad)' : 'var(--bg-soft)'}; border: 1px solid ${active ? 'var(--brand-blue)' : 'var(--border-strong)'}; color: ${active ? '#fff' : 'var(--ink-dim)'}; font-family:'JetBrains Mono',monospace; font-size:10px; letter-spacing:0.1em; text-transform:uppercase; text-align:center; border-radius:3px; transition:all 0.15s;">${escapeHtml(s.name)}</div>`;
            }).join('')}
          </div>
          <div class="inline-note" style="margin-bottom:8px;">Status</div>
          <div style="display:flex; gap:6px; flex-wrap: wrap;">
            ${state.settings.dealStatuses.map(s => {
              const isActive = (d.status || 'active') === s.id;
              const colorClass = s.color ? `chip ${s.color}` : '';
              return `<button class="btn small ${isActive?'primary':'ghost'}" onclick="window._crm.setDealStatus('${d.id}', '${s.id}')">${escapeHtml(s.name)}</button>`;
            }).join('')}
          </div>
        </div>

        ${(d.quoteNumber || d.quoteDate || quoteAtt) ? `
          <div class="panel" style="padding: 16px 20px;">
            <h3 style="margin: 0 0 12px; padding-bottom: 0; border-bottom: none;">Quote / Proposal</h3>
            <div style="display:grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: ${quoteAtt ? '12px' : '0'};">
              ${d.quoteNumber ? `<div><div class="inline-note">Number</div><div style="font-size: 14px; color: var(--ink); margin-top: 3px;">${escapeHtml(d.quoteNumber)}</div></div>` : ''}
              ${d.quoteDate ? `<div><div class="inline-note">Date</div><div style="font-size: 14px; color: var(--ink); margin-top: 3px;">${escapeHtml(fmtDate(d.quoteDate))}</div></div>` : ''}
            </div>
            ${quoteAtt ? `
              <div style="display:flex; align-items:center; gap:10px; padding: 10px 12px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 4px;">
                <span style="font-size:20px;">📄</span>
                <div style="flex:1; min-width: 0;">
                  <div style="font-size:13.5px; color:var(--ink); font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${escapeHtml(quoteAtt.fileName)}</div>
                  <div class="inline-note" style="margin-top:2px;">${escapeHtml(fmtFileSize(quoteAtt.fileSize))}</div>
                </div>
                <button class="btn small" onclick="window._crm.viewAttachment('${quoteAtt.id}')">View</button>
                <button class="btn small ghost" onclick="window._crm.downloadAttachment('${quoteAtt.id}')">Download</button>
              </div>
            ` : '<div class="inline-note">No quote document attached.</div>'}
          </div>
        ` : ''}

        ${renderQuickNote({ dealId: d.id, contactId: d.contactId, companyId: d.companyId })}
        ${renderTimeline(activities, { title: 'Deal history' })}
      </div>
      <div class="record-right">
        <div style="margin-bottom: 14px;">${renderAttachmentsPanel('deal', d.id)}</div>
        <div class="rel-panel">
          <h4>Quick actions</h4>
          <div style="display:flex; flex-direction:column; gap:8px; margin-top:6px;">
            <button class="btn small" onclick="window._crm.editActivity(null, { type: 'call', dealId: '${d.id}', contactId: '${d.contactId||''}', companyId: '${d.companyId||''}' })">Log a call</button>
            <button class="btn small" onclick="window._crm.editActivity(null, { type: 'email', dealId: '${d.id}', contactId: '${d.contactId||''}', companyId: '${d.companyId||''}' })">Log an email</button>
            <button class="btn small" onclick="window._crm.editActivity(null, { type: 'meeting', dealId: '${d.id}', contactId: '${d.contactId||''}', companyId: '${d.companyId||''}' })">Log a meeting</button>
            <button class="btn small" onclick="window._crm.editActivity(null, { type: 'task', dealId: '${d.id}', contactId: '${d.contactId||''}', companyId: '${d.companyId||''}' })">Create task</button>
          </div>
        </div>
      </div>
    </div>
  `;
}, (params) => {
  if (!params || !getDeal(params.id)) return;
  setupQuickNote();
  setupTimelineFilters();
});

// =============================================================
// TICKETS LIST
// =============================================================
function renderTicketRows(list) {
  if (!list.length) return '<tr><td colspan="7"><div class="empty">No tickets.</div></td></tr>';
  return list.map(t => {
    const co = getCompany(t.companyId);
    const c = getContact(t.contactId);
    const chip = t.stage === 'closed' ? 'ok' : 'accent';
    const pChip = t.priority === 'high' ? 'bad' : t.priority === 'medium' ? 'warn' : '';
    return `<tr onclick="window._crm.navigate('ticket-detail', { id: '${t.id}' })">
      <td style="font-weight:600; color:var(--ink);">${escapeHtml(t.subject)}</td>
      <td>${escapeHtml(co?co.name:'—')}</td>
      <td>${escapeHtml(c?contactName(c):'—')}</td>
      <td><span class="chip ${chip}">${escapeHtml(ticketStageName(t.stage))}</span></td>
      <td>${t.priority ? '<span class="chip ' + pChip + '">' + escapeHtml(t.priority.toUpperCase()) + '</span>' : '—'}</td>
      <td>${escapeHtml(fmtDate(new Date(t.createdAt).toISOString()))}</td>
      <td class="row-actions" onclick="event.stopPropagation()"><button class="btn small" onclick="window._crm.editTicket('${t.id}')">Edit</button></td>
    </tr>`;
  }).join('');
}
registerView('tickets', () => `
  <header class="page-head">
    <div><h1 class="page-title">Tick<span class="accent">ets</span></h1><div class="page-sub">${state.tickets.length} total · ${state.tickets.filter(t=>t.stage!=='closed').length} active</div></div>
    <button class="btn primary" onclick="window._crm.editTicket(null)">+ New ticket</button>
  </header>
  <div class="filter-bar">
    <input type="text" class="search" id="t-search" placeholder="Search tickets…" />
    <select id="t-stage">
      <option value="">All stages</option>
      ${state.settings.ticketStages.map(s => `<option value="${s.id}">${escapeHtml(s.name)}</option>`).join('')}
    </select>
    <select id="t-prio">
      <option value="">Any priority</option>
      ${state.settings.priorities.map(p => `<option value="${p}">${p.toUpperCase()}</option>`).join('')}
    </select>
  </div>
  <div class="table-wrap"><div class="table-scroll">
    <table class="data">
      <thead><tr><th>Subject</th><th>Company</th><th>Contact</th><th>Stage</th><th>Priority</th><th>Opened</th><th></th></tr></thead>
      <tbody id="t-tbody">${renderTicketRows(state.tickets)}</tbody>
    </table>
  </div></div>
`, () => {
  const s = document.getElementById('t-search');
  const st = document.getElementById('t-stage');
  const p = document.getElementById('t-prio');
  const apply = () => {
    const q = (s.value||'').toLowerCase();
    const sv = st.value;
    const pv = p.value;
    const filtered = state.tickets.filter(t => {
      if (sv && t.stage !== sv) return false;
      if (pv && t.priority !== pv) return false;
      if (!q) return true;
      return (t.subject + ' ' + (t.description||'')).toLowerCase().includes(q);
    });
    document.getElementById('t-tbody').innerHTML = renderTicketRows(filtered);
  };
  s.addEventListener('input', apply);
  st.addEventListener('change', apply);
  p.addEventListener('change', apply);
});

// =============================================================
// TICKET DETAIL
// =============================================================
registerView('ticket-detail', (params) => {
  const t = getTicket(params && params.id);
  if (!t) return '<div class="empty">Ticket not found.</div>';
  const co = getCompany(t.companyId);
  const c = getContact(t.contactId);
  const activities = state.activities.filter(a => a.ticketId === t.id);
  return `
    <div class="crumbs"><a onclick="window._crm.navigate('tickets')">Tickets</a> / ${escapeHtml(t.subject)}</div>
    <header class="page-head">
      <div>
        <h1 class="page-title">${escapeHtml(t.subject)}</h1>
        <div class="page-sub">${escapeHtml(ticketStageName(t.stage))}${t.priority ? ' · ' + escapeHtml(t.priority.toUpperCase()) + ' PRIORITY' : ''}</div>
      </div>
      <div style="display:flex; gap:8px;">
        <button class="btn" onclick="window._crm.editTicket('${t.id}')">Edit</button>
        ${t.stage !== 'closed' ? `<button class="btn primary" onclick="window._crm.closeTicket('${t.id}')">Close ticket</button>` : ''}
      </div>
    </header>
    <div class="record-layout">
      <div class="record-side">
        <div class="record-avatar" style="background: ${t.priority==='high'?'var(--bad)':'var(--grad)'};">🎫</div>
        <h2 class="record-name">${escapeHtml(t.subject)}</h2>
        <div class="record-role">${co ? escapeHtml(co.name) : '—'}</div>
        <div class="record-props">
          <div class="record-prop"><div class="k">Stage</div><div class="v"><span class="chip ${t.stage==='closed'?'ok':'accent'}">${escapeHtml(ticketStageName(t.stage))}</span></div></div>
          <div class="record-prop"><div class="k">Priority</div><div class="v">${t.priority ? '<span class="chip ' + (t.priority==='high'?'bad':t.priority==='medium'?'warn':'') + '">' + escapeHtml(t.priority.toUpperCase()) + '</span>' : '—'}</div></div>
          <div class="record-prop"><div class="k">Company</div><div class="v">${co ? '<a onclick="window._crm.navigate(\'company-detail\', { id: \'' + co.id + '\' })">' + escapeHtml(co.name) + '</a>' : '—'}</div></div>
          <div class="record-prop"><div class="k">Contact</div><div class="v">${c ? '<a onclick="window._crm.navigate(\'contact-detail\', { id: \'' + c.id + '\' })">' + escapeHtml(contactName(c)) + '</a>' : '—'}</div></div>
          <div class="record-prop"><div class="k">Description</div><div class="v" style="white-space:pre-wrap;">${escapeHtml(t.description || '—')}</div></div>
        </div>
      </div>
      <div class="record-mid">
        ${renderQuickNote({ ticketId: t.id, contactId: t.contactId, companyId: t.companyId })}
        ${renderTimeline(activities, { title: 'Ticket history' })}
      </div>
      <div class="record-right">
        <div class="rel-panel" style="margin-bottom: 14px;">
          <h4>Stage</h4>
          <div style="display:flex; flex-direction:column; gap:6px; margin-top:8px;">
            ${state.settings.ticketStages.map(s => `<button class="btn small ${s.id===t.stage?'primary':''}" onclick="window._crm.setTicketStage('${t.id}', '${s.id}')">${escapeHtml(s.name)}</button>`).join('')}
          </div>
        </div>
        ${renderAttachmentsPanel('ticket', t.id)}
      </div>
    </div>
  `;
}, (params) => {
  if (!params || !getTicket(params.id)) return;
  setupQuickNote();
  setupTimelineFilters();
});

// =============================================================
// ACTIVITIES LIST
// =============================================================
function renderActivityRows(list) {
  if (!list.length) return '<tr><td colspan="6"><div class="empty">No activities.</div></td></tr>';
  return [...list].sort((a,b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    return (b.createdAt||0) - (a.createdAt||0);
  }).map(a => {
    const c = getContact(a.contactId);
    const d = getDeal(a.dealId);
    const t = a.ticketId ? getTicket(a.ticketId) : null;
    const overdue = !a.done && a.date && a.date < todayStr();
    const linked = [c?contactName(c):null, d?d.title:null, t?t.subject:null].filter(Boolean).join(' · ') || '—';
    return `<tr onclick="window._crm.editActivity('${a.id}')">
      <td onclick="event.stopPropagation(); window._crm.toggleActivity('${a.id}')" style="cursor:pointer; text-align:center;">
        ${a.type === 'task' ? `<input type="checkbox" ${a.done?'checked':''} onclick="event.stopPropagation(); window._crm.toggleActivity('${a.id}')" />` : ''}
      </td>
      <td style="${a.done&&a.type==='task'?'text-decoration:line-through; color:var(--ink-muted);':'color:var(--ink);'} font-weight:600;">${escapeHtml(a.subject)}</td>
      <td><span class="chip">${escapeHtml(a.type)}</span></td>
      <td>${escapeHtml(linked)}</td>
      <td style="${overdue?'color:var(--bad); font-weight:600;':''}">${escapeHtml(fmtDate(a.date))} ${overdue?'<span class="inline-note">· overdue</span>':''}</td>
      <td class="row-actions" onclick="event.stopPropagation()"><button class="btn small" onclick="window._crm.editActivity('${a.id}')">Edit</button></td>
    </tr>`;
  }).join('');
}
registerView('activities', () => `
  <header class="page-head">
    <div><h1 class="page-title">Activ<span class="accent">ity log</span></h1><div class="page-sub">${state.activities.length} total · ${state.activities.filter(a => !a.done).length} open</div></div>
    <button class="btn primary" onclick="window._crm.editActivity(null)">+ Log activity</button>
  </header>
  <div class="filter-bar">
    <input type="text" class="search" id="a-search" placeholder="Search…" />
    <select id="a-type">
      <option value="">All types</option>
      <option value="note">Note</option>
      <option value="call">Call</option>
      <option value="email">Email</option>
      <option value="meeting">Meeting</option>
      <option value="task">Task</option>
    </select>
    <select id="a-filter">
      <option value="all">All</option>
      <option value="open">Open</option>
      <option value="done">Done</option>
      <option value="overdue">Overdue</option>
      <option value="today">Today</option>
    </select>
  </div>
  <div class="table-wrap"><div class="table-scroll">
    <table class="data">
      <thead><tr><th style="width:40px;"></th><th>Subject</th><th>Type</th><th>Linked to</th><th>Date</th><th></th></tr></thead>
      <tbody id="a-tbody">${renderActivityRows(state.activities)}</tbody>
    </table>
  </div></div>
`, () => {
  const s = document.getElementById('a-search');
  const f = document.getElementById('a-filter');
  const t = document.getElementById('a-type');
  const apply = () => {
    const q = (s.value||'').toLowerCase();
    const fv = f.value;
    const tv = t.value;
    const filtered = state.activities.filter(a => {
      if (tv && a.type !== tv) return false;
      if (fv === 'open' && a.done) return false;
      if (fv === 'done' && !a.done) return false;
      if (fv === 'overdue' && !(a.date && a.date < todayStr() && !a.done)) return false;
      if (fv === 'today' && a.date !== todayStr()) return false;
      if (!q) return true;
      return ((a.subject||'') + ' ' + (a.notes||'')).toLowerCase().includes(q);
    });
    document.getElementById('a-tbody').innerHTML = renderActivityRows(filtered);
  };
  s.addEventListener('input', apply);
  f.addEventListener('change', apply);
  t.addEventListener('change', apply);
});

// =============================================================
// DATA & SETTINGS
// =============================================================
registerView('data', () => `
  <header class="page-head">
    <div><h1 class="page-title">Data<span class="accent">.</span></h1><div class="page-sub">Backup, restore &amp; settings</div></div>
  </header>
  <div class="panel" style="margin-bottom:18px;">
    <h3>Backup</h3>
    <p style="font-size:13.5px; color:var(--ink-dim); margin:0 0 14px;">Supabase keeps your data safe in the cloud. Export a local copy any time as insurance.</p>
    <div style="display:flex; gap:10px; flex-wrap:wrap;">
      <button class="btn primary" onclick="window._crm.exportData()">Export JSON backup</button>
      <button class="btn" onclick="window._crm.exportCSV()">Export CSV</button>
    </div>
  </div>
  <div class="panel" style="margin-bottom:18px;">
    <h3>Import legacy backup</h3>
    <p style="font-size:13.5px; color:var(--ink-dim); margin:0 0 14px;">
      Paste the JSON from the old single-file CRM here to migrate your data. (Open the old file, DevTools → Console, run:
      <code style="color:var(--accent);">copy(localStorage.getItem('igv_crm_v1'))</code>)
    </p>
    <div class="field"><label>Legacy backup JSON</label><textarea id="legacy-import" placeholder="{...}" style="min-height:120px;"></textarea></div>
    <button class="btn primary" id="import-legacy-btn" onclick="window._crm.importLegacy()">Import legacy data</button>
  </div>
  <div class="panel" style="margin-bottom:18px;">
    <h3>Summary</h3>
    <div class="stat-grid" style="margin-bottom:0;">
      <div class="stat"><div class="k">Companies</div><div class="v">${state.companies.length}</div></div>
      <div class="stat"><div class="k">Contacts</div><div class="v">${state.contacts.length}</div></div>
      <div class="stat"><div class="k">Deals</div><div class="v">${state.deals.length}</div></div>
      <div class="stat"><div class="k">Tickets</div><div class="v">${state.tickets.length}</div></div>
    </div>
  </div>
  <div class="panel">
    <h3>Settings</h3>
    <div class="field-row">
      <div class="field"><label>Currency code</label><input type="text" id="s-currency" value="${escapeHtml(state.settings.currency)}" maxlength="5" />
        <div class="help">e.g. ZAR, USD, EUR. Changes are local to your browser.</div>
      </div>
      <div class="field"></div>
    </div>
    <button class="btn primary" onclick="window._crm.saveSettings()">Save settings</button>
  </div>
`);

function exportData() {
  const payload = {
    companies: state.companies, contacts: state.contacts,
    deals: state.deals, tickets: state.tickets, activities: state.activities,
    settings: state.settings, exportedAt: new Date().toISOString()
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'igv-crm-backup-' + todayStr() + '.json'; a.click();
  URL.revokeObjectURL(url);
  toast('Backup exported');
}
function exportCSV() {
  const rows = [['Type','Name','Title','Email','Phone','Company','Industry','Segment']];
  state.contacts.forEach(c => {
    const co = getCompany(c.companyId);
    rows.push(['contact', contactName(c), c.title||'', c.email||'', c.phone||'', co?co.name:'', co?co.industry:'', co?co.segment:'']);
  });
  state.companies.forEach(co => rows.push(['company', co.name, '', '', co.phone||'', co.name, co.industry||'', co.segment||'']));
  const csv = rows.map(r => r.map(v => { const s = String(v==null?'':v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g,'""') + '"' : s; }).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'igv-crm-export-' + todayStr() + '.csv'; a.click();
  URL.revokeObjectURL(url);
  toast('CSV exported');
}
function saveSettings() {
  state.settings.currency = document.getElementById('s-currency').value.trim() || 'ZAR';
  render();
  toast('Settings saved');
}
async function importLegacy() {
  const text = (document.getElementById('legacy-import').value || '').trim();
  if (!text) { toast('Paste the legacy backup JSON first', true); return; }
  if (!confirm('This will add all the legacy records to your Supabase database. Continue?')) return;
  const btn = document.getElementById('import-legacy-btn');
  btn.disabled = true;
  btn.textContent = 'Importing…';
  try {
    const result = await importLegacyBackup(text);
    toast(`Imported ${result.companies} companies, ${result.contacts} contacts, ${result.deals} deals, ${result.tickets} tickets, ${result.activities} activities`);
    const { refreshAll } = await import('./lib/store.js');
    await refreshAll();
    render();
  } catch (e) {
    console.error(e);
    toast('Import failed: ' + e.message, true);
  } finally {
    btn.disabled = false;
    btn.textContent = 'Import legacy data';
  }
}

// Export data module functions for window binding
export const dataActions = { exportData, exportCSV, saveSettings, importLegacy };

// =============================================================
// SETTINGS (admin only) - FX rates and workspace settings
// =============================================================
registerView('settings', () => {
  if (!isAdmin()) {
    return `<div class="empty">Admin access required.</div>`;
  }
  const fx = state.fxRates;
  return `
    <header class="page-head">
      <div>
        <h1 class="page-title">Set<span class="accent">tings</span></h1>
        <div class="page-sub">Workspace level configuration · admin only</div>
      </div>
    </header>

    <div class="panel" style="max-width: 720px;">
      <h3>Foreign exchange rates</h3>
      <p style="font-size: 13px; color: var(--ink-dim); margin: 0 0 16px;">
        Used to convert deals into a single display currency on the dashboard. Rates are quoted as <strong>ZAR per foreign unit</strong>.
        Update them whenever rates move meaningfully (typically monthly). All team members see the same rates.
      </p>

      <div class="field-row">
        <div class="field">
          <label>ZAR per 1 USD</label>
          <input type="number" id="fx-usd" value="${escapeHtml(fx.zarPerUsd || '')}" step="0.01" min="0" placeholder="e.g. 18.50" />
          <div class="help">How many South African Rand 1 US Dollar buys.</div>
        </div>
        <div class="field">
          <label>ZAR per 1 EUR</label>
          <input type="number" id="fx-eur" value="${escapeHtml(fx.zarPerEur || '')}" step="0.01" min="0" placeholder="e.g. 19.80" />
          <div class="help">How many South African Rand 1 Euro buys.</div>
        </div>
      </div>
      <div class="field">
        <label>As of date</label>
        <input type="date" id="fx-asof" value="${escapeHtml(fx.asOfDate || todayStr())}" />
        <div class="help">When you sourced these rates. Shown next to the dashboard toggle.</div>
      </div>

      <div style="display:flex; justify-content:flex-end; gap: 10px; margin-top: 14px;">
        <button class="btn primary" id="save-fx-btn" onclick="window._crm.saveFxRates()">Save rates</button>
      </div>
    </div>
  `;
});

export async function saveFxRates() {
  const usd = parseFloat(document.getElementById('fx-usd').value);
  const eur = parseFloat(document.getElementById('fx-eur').value);
  const asOf = document.getElementById('fx-asof').value;
  if (!(usd > 0)) { toast('Enter a valid ZAR per USD rate', true); return; }
  if (!(eur > 0)) { toast('Enter a valid ZAR per EUR rate', true); return; }
  const btn = document.getElementById('save-fx-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    await runAsync(() => store.fxRates.save({ zarPerUsd: usd, zarPerEur: eur, asOfDate: asOf }), 'FX rates updated');
    render();
  } catch (_) {
    if (btn) { btn.disabled = false; btn.textContent = 'Save rates'; }
  }
}

// =============================================================
// USERS (admin only) - team member management
// =============================================================
registerView('users', () => {
  if (!isAdmin()) {
    return `<div class="empty">Admin access required.</div>`;
  }
  const myId = state.currentProfile ? state.currentProfile.id : null;
  return `
    <header class="page-head">
      <div>
        <h1 class="page-title">Us<span class="accent">ers</span></h1>
        <div class="page-sub">${state.profiles.length} member${state.profiles.length===1?'':'s'} · ${state.profiles.filter(p=>p.isActive).length} active</div>
      </div>
      <button class="btn primary" onclick="window._crm.inviteUserModal()">+ Invite user</button>
    </header>

    <div class="panel" style="padding: 0; overflow: hidden;">
      <table class="data" style="margin: 0;">
        <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Status</th><th>Joined</th><th></th></tr></thead>
        <tbody>
          ${state.profiles.map(p => `
            <tr>
              <td style="font-weight:600; color: var(--ink);">${escapeHtml(p.email)}${p.id===myId ? ' <span class="inline-note" style="font-weight: normal;">(you)</span>' : ''}</td>
              <td>${escapeHtml(p.fullName || '—')}</td>
              <td>${p.isAdmin ? '<span class="chip accent">Admin</span>' : '<span class="chip">Member</span>'}</td>
              <td>${p.isActive ? '<span class="chip ok">Active</span>' : '<span class="chip muted">Deactivated</span>'}</td>
              <td>${escapeHtml(fmtDate(new Date(p.createdAt).toISOString()))}</td>
              <td class="row-actions">
                ${p.id === myId
                  ? '<span class="inline-note">Manage your own access in Supabase</span>'
                  : `
                    <button class="btn small" onclick="window._crm.toggleAdmin('${p.id}', ${!p.isAdmin})">${p.isAdmin ? 'Demote' : 'Make admin'}</button>
                    <button class="btn small ${p.isActive ? 'danger' : ''}" onclick="window._crm.toggleActive('${p.id}', ${!p.isActive})">${p.isActive ? 'Deactivate' : 'Reactivate'}</button>
                  `}
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>

    <div class="panel" style="margin-top: 18px; max-width: 720px;">
      <h3>How invites work</h3>
      <p style="font-size: 13px; color: var(--ink-dim); margin: 0;">
        When you invite a user, Supabase emails them a magic link. They click it, set a password, and gain access immediately.
        Deactivating a user revokes their access on their next request — they'll be signed out the next time their browser refreshes.
        To permanently delete a user account, do it in the Supabase Authentication dashboard.
      </p>
    </div>
  `;
});

export function inviteUserModal() {
  const body = `
    <div class="field"><label>Email</label><input type="email" id="inv-email" placeholder="colleague@igvsystems.net" autofocus /></div>
    <div class="field"><label>Full name (optional)</label><input type="text" id="inv-name" placeholder="Jane Mokoena" /></div>
    <div class="field" style="display:flex; align-items:center; gap:8px;">
      <input type="checkbox" id="inv-admin" style="width:auto;" />
      <label for="inv-admin" style="margin:0; cursor:pointer;">Make this user an admin</label>
    </div>
    <div class="help" style="margin-top: 8px;">An invitation email will be sent. They'll set their own password.</div>
  `;
  const footer = `
    <span></span>
    <div class="right">
      <button class="btn ghost" onclick="window._crm.closeModal()">Cancel</button>
      <button class="btn primary" id="send-invite-btn" onclick="window._crm.sendInvite()">Send invite</button>
    </div>
  `;
  showModal({ title: 'Invite a team member', body, footer });
}

export async function sendInvite() {
  const email = document.getElementById('inv-email').value.trim();
  const name = document.getElementById('inv-name').value.trim();
  const makeAdmin = document.getElementById('inv-admin').checked;
  if (!email || !email.includes('@')) { toast('Enter a valid email', true); return; }
  const btn = document.getElementById('send-invite-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  try {
    await runAsync(() => store.profiles.invite(email, name, makeAdmin), 'Invitation sent to ' + email);
    closeModal();
    render();
  } catch (e) {
    if (btn) { btn.disabled = false; btn.textContent = 'Send invite'; }
  }
}

export async function toggleAdmin(id, makeAdmin) {
  const p = state.profiles.find(x => x.id === id);
  if (!p) return;
  if (!confirm(`${makeAdmin ? 'Promote' : 'Demote'} ${p.email}?`)) return;
  await runAsync(() => store.profiles.setAdmin(id, makeAdmin), makeAdmin ? 'Promoted to admin' : 'Admin demoted');
  render();
}

export async function toggleActive(id, makeActive) {
  const p = state.profiles.find(x => x.id === id);
  if (!p) return;
  const verb = makeActive ? 'Reactivate' : 'Deactivate';
  if (!confirm(`${verb} ${p.email}? ${makeActive ? 'They will regain access immediately.' : 'They will lose access on their next request.'}`)) return;
  await runAsync(() => store.profiles.setActive(id, makeActive), makeActive ? 'User reactivated' : 'User deactivated');
  render();
}

export const adminActions = { saveFxRates, inviteUserModal, sendInvite, toggleAdmin, toggleActive };
